/**
 * The in-game line score (ISSUE-989, Scores UX spec v1.0 section 7.4),
 * titled "Line score" so it never reads like the Box score tab.
 *
 * A header row, then away, then home; rows 36 tall, cells 32 wide. The team
 * column is frozen (max 96): a 20px mark, 8, then the abbreviation in `caption`
 * 600. The period columns scroll sideways inside the card and open anchored
 * to the right edge, so the latest period and the totals are what a fan sees
 * first. Header cells are `small` `textTertiary`, values `body`, the totals
 * `bodyStrong`, all tabular. A final's winner row is bold and the other row
 * muted (card R4).
 *
 * While the game is live the current period's column is tinted
 * `primaryTint` and its header turns `primary`. That tint is not a selection
 * indicator, so theme.mdc's "never a tint alone" does not apply. The server
 * sends no current period key, so the current period is the right-most
 * period the server has filled for either team: a layout reading of the
 * server's own cells, not a computed value.
 *
 * Values render exactly as sent. A period neither team has a value for yet
 * is blank; a period with only one team's value shows "-" for the other.
 */
import { cn } from "@sportsmet/ui";
import { type ReactNode, useLayoutEffect, useRef } from "react";

import { TeamDisc } from "../scores/TeamDisc";
import { GameSectionCard } from "./GameSectionCard";
import type {
  typeLineScore,
  typeLineScoreCell,
  typeMatchupTeamMark,
} from "./matchupTypes";

export const LINE_SCORE_TITLE = "Line score";
export const LINE_SCORE_MISSING_CELL = "-";

const hasValue = (value: string | null | undefined): value is string =>
  typeof value === "string" && value !== "";

/** A cell's text: the server's value, "" for a period not played, "-" for
 *  one the other team has a value for. */
export const lineScoreCellText = (
  cell: typeLineScoreCell,
  side: "away" | "home",
): string => {
  const own = side === "away" ? cell.awayValue : cell.homeValue;
  const other = side === "away" ? cell.homeValue : cell.awayValue;
  if (hasValue(own)) return own;
  return hasValue(other) ? LINE_SCORE_MISSING_CELL : "";
};

/** The key of the right-most period either team has a value for, or null. */
export const currentPeriodKey = (lineScore: typeLineScore): string | null => {
  for (let index = lineScore.periods.length - 1; index >= 0; index -= 1) {
    const cell = lineScore.periods[index];
    if (
      cell !== undefined &&
      (hasValue(cell.awayValue) || hasValue(cell.homeValue))
    )
      return cell.key;
  }
  return null;
};

const rowClass = (
  winnerSide: "away" | "home" | null,
  side: "away" | "home",
): string => {
  if (winnerSide === null) {
    return "text-body text-ink";
  }
  return winnerSide === side
    ? "text-body-strong text-ink"
    : "text-body text-ink-secondary";
};

const TEAM_COLUMN_CLASS =
  "sticky left-0 z-10 max-w-24 bg-surface pr-2 pl-3 text-left";
const CELL_CLASS = "h-9 min-w-8 px-1 text-center";

export const LineScoreSection = ({
  lineScore,
  away,
  home,
  isLive = false,
}: {
  /** The server's section title. Spec 7.4 names this card "Line score". */
  title?: string;
  lineScore: typeLineScore;
  away: typeMatchupTeamMark;
  home: typeMatchupTeamMark;
  isLive?: boolean;
}): ReactNode => {
  const winnerSide = lineScore.winnerSide ?? null;
  const currentKey = isLive ? currentPeriodKey(lineScore) : null;
  const scrollRef = useRef<HTMLDivElement>(null);
  // Anchor the periods to the right edge on open, so the latest period and
  // the totals are on screen at phone width.
  useLayoutEffect(() => {
    const element = scrollRef.current;
    if (element === null) return;
    element.scrollLeft = element.scrollWidth;
  }, [lineScore.periods.length]);
  const sides = [
    { side: "away", team: away },
    { side: "home", team: home },
  ] as const;
  return (
    <GameSectionCard testId="matchup-line-score" title={LINE_SCORE_TITLE} bleed>
      <div
        ref={scrollRef}
        data-testid="line-score-scroll"
        data-anchor="end"
        className="overflow-x-auto pr-3"
      >
        <table className="w-full border-separate border-spacing-0 tabular-nums">
          <thead>
            <tr>
              <th scope="col" className={cn(TEAM_COLUMN_CLASS, "h-9")}>
                <span className="sr-only">Team</span>
              </th>
              {lineScore.periods.map((cell) => (
                <th
                  key={cell.key}
                  scope="col"
                  data-current={cell.key === currentKey ? "true" : undefined}
                  className={cn(
                    CELL_CLASS,
                    "text-small",
                    cell.key === currentKey
                      ? "bg-primary-tint text-primary"
                      : "text-ink-tertiary",
                  )}
                >
                  {cell.label}
                </th>
              ))}
              {lineScore.totals.map((cell) => (
                <th
                  key={cell.key}
                  scope="col"
                  className={cn(CELL_CLASS, "text-small text-ink-tertiary")}
                >
                  {cell.label}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {sides.map(({ side, team }) => (
              <tr
                key={side}
                data-testid="line-score-row"
                data-side={side}
                data-winner={winnerSide === side ? "true" : undefined}
                className={rowClass(winnerSide, side)}
              >
                <th
                  scope="row"
                  className={cn(TEAM_COLUMN_CLASS, "h-9 font-normal")}
                >
                  <span className="flex items-center gap-2">
                    <TeamDisc
                      plateText={team.plateText}
                      logoUrl={team.logoUrl}
                      size="mini"
                      testId="team-mark"
                      imageTestId="team-mark-image"
                    />
                    <span className="text-caption text-ink">
                      {team.abbreviation ?? team.plateText}
                    </span>
                  </span>
                </th>
                {lineScore.periods.map((cell) => (
                  <td
                    key={cell.key}
                    data-testid="line-score-cell"
                    data-current={cell.key === currentKey ? "true" : undefined}
                    className={cn(
                      CELL_CLASS,
                      cell.key === currentKey ? "bg-primary-tint" : undefined,
                    )}
                  >
                    {lineScoreCellText(cell, side)}
                  </td>
                ))}
                {lineScore.totals.map((cell) => (
                  <td
                    key={cell.key}
                    data-testid="line-score-cell"
                    className={cn(CELL_CLASS, "text-body-strong")}
                  >
                    {lineScoreCellText(cell, side)}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </GameSectionCard>
  );
};
